import { ArrowCounterClockwiseIcon, ExportIcon } from '@phosphor-icons/react'
import { mappedColors } from '@pixee/design-system'
import { useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import {
  useFindingQuickViewState,
  useGetScanAndRepository,
  useGetScanLifecycleState,
  useSearchQueryParam,
  useSortDirectionQueryParam,
  useSortQueryParam,
} from '../hooks/scan-hooks.ts'
import { useExportCsv } from '../hooks/use-export-csv.tsx'
import { useGetScaleFromHalLink } from '../utils/api-client/api-hooks.ts'
import { isScaTool } from '../utils/detector-utils.ts'
import { getEnvironmentData } from '../utils/hooks/get-environment-data.ts'
import { GhostButton } from './buttons/ghost-button.tsx'
import { FindingsCountLabel } from './findings-count-label.tsx'
import {
  FindingsSortDropdownMenu,
  mapSortDirectionToApi,
  mapSortOptionToApi,
  type FindingsSortDropdownMenuProps,
} from './findings-sort-dropdown.tsx'
import { TextInput, type TextInputProps } from './input/text-input.tsx'
import { QuickViewSelector } from './menus/quick-view-selector'
import * as styles from './scan-findings-table-controls.css.ts'
import { ActiveFilterChipsPure, toActiveFilterChips, type ActiveFilterChip } from './tables/active-filter-chips.tsx'
import {
  getAdditionalFilterCount,
  useFilterStateStore,
  useGetScanFindingsWithFilters,
  usePerCategoryStatistics,
  type FilterCategory,
} from './tables/filter-state-store.tsx'
import { getFindingQuickViewConfig, type FindingQuickViewId } from './tables/finding-quick-view-config'
import {
  FindingsTableDropdownFilter,
  type FindingsTableDropdownFilterProps,
} from './tables/findings-table-dropdown-filter.tsx'
import { usePagination } from './tables/use-pagination.ts'
import { ActiveFiltersTag } from './tags/active-filters-tag.tsx'
import { ToolIcon } from './third-party-branding/tool-icon.tsx'
import { TooltipWithHover } from './tooltip.tsx'

// #region Wrapper

export type ScanFindingsTableControlsProps = {
  scanId: string
}

export const ScanFindingsTableControls: React.FC<ScanFindingsTableControlsProps> = ({ scanId }) => {
  const props = useScanFindingsTableControlsBase({ scanId })
  return <ScanFindingsTableControlsPure {...props} />
}

// #endregion

// #region Hook

type UseScanFindingsTableControlsBaseOptions = {
  scanId: string
}

function useScanFindingsTableControlsBase({ scanId }: UseScanFindingsTableControlsBaseOptions) {
  const { scan } = useGetScanAndRepository({ scanId })
  const filterState = useFilterStateStore(useShallow(state => state))
  const [search, setSearch] = useSearchQueryParam()
  const [selectedSort, setSelectedSort] = useSortQueryParam()
  const [isAscending, setIsAscending] = useSortDirectionQueryParam()
  const { selectedQuickView, setSelectedQuickView, quickViews } = useFindingQuickViewState({ scanId })
  const { scanLifecycleState } = useGetScanLifecycleState({ scanId })
  const environmentData = getEnvironmentData()

  const { data: scale } = useGetScaleFromHalLink({
    path: scan?._links?.scale?.href,
  })

  const isSca = scan ? isScaTool(scan.detector) : false
  const isInitialOrFirstBatch =
    scanLifecycleState?.type === 'initial-unanalyzed-scan' ||
    scanLifecycleState?.type === 'first-analysis-batch-in-progress'
  const effectiveQuickView = isInitialOrFirstBatch ? ('all-findings' as const) : selectedQuickView
  const quickViewConfig = getFindingQuickViewConfig(effectiveQuickView)

  const searchInputProps = useMemo(
    () =>
      ({
        value: search,
        onChange: setSearch,
        ariaLabel: 'Search findings',
        type: 'search',
        placeholder: 'Search findings',
        size: 'small',
        debounce: 200,
      }) satisfies TextInputProps,
    [search, setSearch]
  )

  const findingsSortDropdownMenuProps = useMemo(
    () =>
      ({
        selectedSort,
        onSortChange: setSelectedSort,
        isAscending,
        onSortDirectionChange: setIsAscending,
      }) satisfies FindingsSortDropdownMenuProps,
    [selectedSort, setSelectedSort, isAscending, setIsAscending]
  )

  const groups = useMemo(() => {
    if (isSca) {
      return {
        ...quickViewConfig.visibleGroups,
        analyzed: quickViewConfig.visibleGroups?.analyzed ?? 'hidden',
        hasFix: 'hidden',
        hasTriage: 'hidden',
        hasScaAnalysis: quickViewConfig.visibleGroups?.hasScaAnalysis ?? 'enabled',
        classification: 'hidden',
        scaClassification: quickViewConfig.visibleGroups?.scaClassification ?? 'enabled',
        fixConfidence: 'hidden',
        severityUpdate: 'hidden',
        suggestedSeverity: 'hidden',
        severity: quickViewConfig.visibleGroups?.severity ?? 'enabled',
      } satisfies FindingsTableDropdownFilterProps['groups']
    } else {
      return {
        ...quickViewConfig.visibleGroups,
        analyzed: quickViewConfig.visibleGroups?.analyzed ?? 'hidden',
        hasFix: quickViewConfig.visibleGroups?.hasFix ?? 'hidden',
        hasTriage: quickViewConfig.visibleGroups?.hasTriage ?? 'hidden',
        hasScaAnalysis: 'hidden',
        classification: quickViewConfig.visibleGroups?.classification ?? 'enabled',
        scaClassification: 'hidden',
        fixConfidence: quickViewConfig.visibleGroups?.fixConfidence ?? 'enabled',
        severityUpdate: quickViewConfig.visibleGroups?.severityUpdate ?? 'enabled',
        severity: quickViewConfig.visibleGroups?.severity ?? 'enabled',
      } satisfies FindingsTableDropdownFilterProps['groups']
    }
  }, [isSca, quickViewConfig.visibleGroups])

  const { pagination } = usePagination({ defaultPageIndex: 0, defaultPageSize: 10 })
  const { data: paginatedFindings } = useGetScanFindingsWithFilters({
    scanId,
    pagination,
    filterState,
    search: search || undefined,
    sort: mapSortOptionToApi(selectedSort),
    order: mapSortDirectionToApi(isAscending),
    embed: ['items.representative-results.latest-patch'],
  })

  const visibleCategories = useMemo(() => {
    const categories: FilterCategory[] = []
    if (groups.classification && groups.classification !== 'hidden') categories.push('classification')
    if (groups.fixResult && groups.fixResult !== 'hidden') categories.push('fixResult')
    if (groups.fixConfidence && groups.fixConfidence !== 'hidden') categories.push('fixConfidence')
    if (groups.scaClassification && groups.scaClassification !== 'hidden') categories.push('scaClassification')
    return categories
  }, [groups.classification, groups.fixResult, groups.fixConfidence, groups.scaClassification])

  const categoryStatistics = usePerCategoryStatistics({
    scanId,
    filterState,
    search: search || undefined,
    sort: mapSortOptionToApi(selectedSort),
    order: mapSortDirectionToApi(isAscending),
    visibleCategories,
  })

  const findingsTableDropdownFilterProps = useMemo(
    () =>
      ({
        ...filterState,
        scale,
        groups,
        statistics: paginatedFindings,
        categoryStatistics,
        resetIcon: <ArrowCounterClockwiseIcon size={16} style={{ color: mappedColors.neutral.halo }} />,
        resetLabel: 'Restore default filters',
        contentClassName: styles.findingsTableDropdownFilter,
        toolSeverityIcon: <ToolIcon tool={scan?.detector} size="small" withAriaLabel={true} />,
        suggestedSeverityIcon: <img src={environmentData.logoIconHref} height={16} alt="" />,
      }) satisfies FindingsTableDropdownFilterProps,
    [filterState, scale, groups, paginatedFindings, categoryStatistics, scan?.detector, environmentData.logoIconHref]
  )

  const { handleExportCsv, isExportingCsv } = useExportCsv({
    scanId,
    filterState,
    search,
    sort: mapSortOptionToApi(selectedSort),
    order: mapSortDirectionToApi(isAscending),
  })

  const additionalFilterCount = getAdditionalFilterCount(filterState, quickViewConfig.defaultFilterState)

  // Measured against the same baseline as `additionalFilterCount`, so a quick view's own locked
  // filters do not appear as chips the user could remove but not restore.
  const activeFilterChips = useMemo(
    () => toActiveFilterChips({ filterState, defaultFilterState: quickViewConfig.defaultFilterState }),
    [filterState, quickViewConfig.defaultFilterState]
  )

  return {
    activeFilterChips,
    searchInputProps,
    findingsSortDropdownMenuProps,
    findingsTableDropdownFilterProps,
    quickViews,
    selectedQuickView,
    onSelectedQuickViewChange: setSelectedQuickView,
    currentCount: paginatedFindings?.total,
    showQuickViewSelector:
      scanLifecycleState?.type === 'findings-with-analysis-complete-and-in-progress' ||
      scanLifecycleState?.type === 'findings-with-analysis-complete',
    onExportCsv: handleExportCsv,
    isExportingCsv,
    additionalFilterCount,
    onReset: filterState.resetFilters,
  } satisfies ScanFindingsTableControlsPureProps
}

// #endregion

// #region Pure Component

type ScanFindingsTableControlsPureProps = {
  searchInputProps: TextInputProps
  findingsSortDropdownMenuProps: FindingsSortDropdownMenuProps
  findingsTableDropdownFilterProps: FindingsTableDropdownFilterProps
  quickViews: ReturnType<typeof useFindingQuickViewState>['quickViews']
  selectedQuickView: FindingQuickViewId
  onSelectedQuickViewChange: (view: FindingQuickViewId) => unknown
  currentCount?: number
  showQuickViewSelector: boolean
  onExportCsv: () => unknown
  isExportingCsv: boolean
  additionalFilterCount: number
  activeFilterChips: ActiveFilterChip[]
  onReset: () => unknown
}

const ScanFindingsTableControlsPure: React.FC<ScanFindingsTableControlsPureProps> = ({
  searchInputProps,
  findingsSortDropdownMenuProps,
  findingsTableDropdownFilterProps,
  quickViews,
  selectedQuickView,
  onSelectedQuickViewChange,
  currentCount,
  showQuickViewSelector,
  onExportCsv,
  isExportingCsv,
  additionalFilterCount,
  activeFilterChips,
  onReset,
}) => (
  <div className={styles.tableControlsSection}>
    <div className={styles.tableControls} aria-label="Findings Table Controls">
      <div className={styles.quickViewGroup}>
        {showQuickViewSelector ? (
          <QuickViewSelector<FindingQuickViewId>
            quickViews={quickViews}
            selectedQuickView={selectedQuickView}
            onSelectedQuickViewChange={onSelectedQuickViewChange}
            currentCount={currentCount}
          />
        ) : (
          <FindingsCountLabel count={currentCount} />
        )}
        {additionalFilterCount > 0 && <ActiveFiltersTag onReset={onReset} />}
      </div>
      <div className={styles.controlGroup}>
        <div className={styles.searchInput}>
          <TextInput {...searchInputProps} />
        </div>
        <FindingsSortDropdownMenu {...findingsSortDropdownMenuProps} />
        <FindingsTableDropdownFilter {...findingsTableDropdownFilterProps} />
        <TooltipWithHover
          trigger={
            <GhostButton
              type="secondary"
              size="extra-small"
              onClick={onExportCsv}
              state={isExportingCsv ? 'disabled' : 'default'}
              ariaLabel="Download CSV"
            >
              <ExportIcon size={20} />
            </GhostButton>
          }
          side="top"
        >
          Export current view to CSV
        </TooltipWithHover>
      </div>
    </div>
    {activeFilterChips.length > 0 && (
      <div className={styles.activeFilterChipsRow}>
        <ActiveFilterChipsPure chips={activeFilterChips} />
      </div>
    )}
  </div>
)

// #endregion
